import React, { useState, useEffect } from 'react';
import ArticleAnalysisCard from './ArticleAnalysisCard';

// Custom hook to expose pagination data to parent components
export const useArticlesPagination = () => {
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10,
    hasMore: false
  });
  
  return [paginationData, setPaginationData];
};

const ArticlesList = ({ filter = {}, isAdmin = false }) => {
  console.log(`ArticlesList component initialized with isAdmin=${isAdmin}`);
  // Debug check for admin status
  if (isAdmin) {
    console.log('This user is an admin, should see Pending Review articles');
  }
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10
  });
  
  // Create a ref to track API requests in progress and prevent duplicate calls
  const fetchInProgress = React.useRef(false);
  
  const fetchArticles = async (page = 1) => {
    // If a fetch is already in progress, don't start another one
    if (fetchInProgress.current) {
      console.log("Skipping duplicate fetch request");
      return;
    }
    
    try {
      fetchInProgress.current = true;
      setLoading(true);
      
      // IMPORTANT: Force admin parameter for logged-in users to ensure they can see all articles
      const adminStatus = isAdmin === true;
      
      // Build query params
      const params = new URLSearchParams({
        page,
        pageSize: pageSizeRef.current,
        admin: adminStatus.toString() // Pass admin status to API, ensure it's stringified correctly
      });
      
      console.log(`ArticlesList - Fetching with admin=${adminStatus}, status=${filter?.status || 'all'}`);
      
      // Add optional filters if they exist
      if (filter?.status) params.append('status', filter.status);
      if (filter?.publisher) params.append('publisher', filter.publisher);
      if (filter?.author) params.append('author', filter.author);
      
      console.log(`Fetching articles with params: ${params.toString()}`);
      const response = await fetch(`/api/articles/list?${params.toString()}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Failed to fetch articles');
      }
      
      const data = await response.json();
      setArticles(data.articles);
      setPagination(prev => ({
        ...prev,
        currentPage: data.pagination.currentPage,
        totalPages: data.pagination.totalPages,
      }));
    } catch (err) {
      console.error("Error fetching articles:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      // Allow new fetches only after a short delay to prevent rapid consecutive calls
      setTimeout(() => {
        fetchInProgress.current = false;
      }, 300);
    }
  };
  
  // Use a ref to track if this is the first render
  const isInitialMount = React.useRef(true);
  // Use a ref to store the pagination size to avoid dependency loops
  const pageSizeRef = React.useRef(pagination.pageSize);
  // Use a ref to store the previous filter
  const prevFilterRef = React.useRef(filter);
  
  // Get the current page from URL params
  const [searchParams] = React.useState(() => new URLSearchParams(window.location.search));
  const urlPage = React.useMemo(() => {
    return parseInt(searchParams.get('page') || '1', 10);
  }, [searchParams]);
  
  // Update pagination when URL changes
  React.useEffect(() => {
    setPagination(prev => ({
      ...prev,
      currentPage: urlPage
    }));
  }, [urlPage]);
  
  useEffect(() => {
    // Create a stable reference to the fetch function
    const fetchData = () => {
      // Skip if a fetch is already in progress
      if (fetchInProgress.current) return;
      fetchArticles(urlPage);
    };
    
    // Skip the first render to avoid double loading
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchData();
      return;
    }
    
    // Compare current filter with previous filter to avoid unnecessary reloads
    const prevFilter = prevFilterRef.current;
    const filterChanged = 
      prevFilter?.status !== filter?.status ||
      prevFilter?.publisher !== filter?.publisher ||
      prevFilter?.author !== filter?.author;
    
    // Only fetch if filter has changed
    if (filterChanged) {
      prevFilterRef.current = filter;
      fetchData();
    }
    
    // Cleanup function
    return () => {
      // Cancel any pending requests
      fetchInProgress.current = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, urlPage]); // We need to refetch when URL page changes
  
  const handlePageChange = (newPage) => {
    // Prevent multiple rapid clicks
    if (fetchInProgress.current || loading) {
      return;
    }
    
    // Scroll to top for better user experience
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Update URL with new page number
    const url = new URL(window.location);
    
    // For SEO, don't add page=1 to URL
    if (newPage === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', newPage.toString());
    }
    
    // Update browser history
    window.history.pushState({}, '', url);
    
    // Fetch articles for the new page
    fetchArticles(newPage);
  };
  
  if (loading && articles.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-80">
        <div className="animate-pulse flex space-x-4">
          <div className="text-gray-600">Loading articles...</div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
        <p className="font-bold">Error loading articles</p>
        <p>{error}</p>
      </div>
    );
  }
  
  if (articles.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 text-gray-800 px-4 py-5 rounded mb-4 text-center">
        <p className="text-lg font-medium">No articles found</p>
        <p className="text-gray-600">Try adjusting your filters or submit a new article for analysis.</p>
      </div>
    );
  }
  
  return (
    <div>
    
      <div className="grid grid-cols-1 gap-6 mb-6">
        {articles.map(article => (
          <ArticleAnalysisCard key={article.id} article={article} />
        ))}
      </div>
      
      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <nav className="inline-flex rounded-md shadow" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              aria-label="Previous page"
              className={`px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                pagination.currentPage === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            
            <div className="px-4 py-1 border-t border-b border-gray-300 bg-white text-sm pagination-info">
              Page {pagination.currentPage} of {pagination.totalPages}
            </div>
            
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              aria-label="Next page"
              className={`px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                pagination.currentPage === pagination.totalPages
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default ArticlesList;